/**
 * Live Leaderboard Panel
 * Displays team rankings with distance to finish
 */

const TEAM_FLAGS = {
  "44West": "CH",
  "Aire Wave": "GB",
  "Allons-Y": "US",
  "Atlantic Highway": "GB",
  "Barnaby Cecil": "GB",
  "Bestgole": "NL",
  "Call To Earth": "GB",
  "Cross Rower": "MT",
  "Force Atlantic": "GB",
  "Force Horizon": "GB",
  "Get Busy Rowing": "GB",
  "Give Us A Wave": "GB",
  "Gone Fishin": "GB",
  "Greens2Blue": "GB",
  "Hansel And Gretel": "DE",
  "Hms Oardacious": "GB",
  "Just One More Row": "GB",
  "Kiwi Fondue": "CH",
  "Lightship": "GB",
  "Madlantic": "BE",
  "Merched Y Mor": "XW",
  "Ocean Mavericks": "ZA",
  "Ocean Oarsmen": "GB",
  "Oceanida": "MX",
  "Offshoare": "DE",
  "Per Ardua 21": "GB",
  "Row Aurora- Hometown Row": "GB",
  "Row For Change": "DK",
  "Row With The Flow": "GB",
  "Scotland The Wave": "XS",
  "Seas Life": "GB",
  "Secondoary Fun": "GB",
  "Southsea Scullers": "GB",
  "Stelantic": "CH",
  "Team Rowtalia": "EE",
  "Team Seneca Navy": "US",
  "The Entrepreneur Ship 2025": "GB",
  "The Jasper": "IE",
  "The Old See Dogs": "GB",
  "The Only Way Is Rowing": "GB",
  "The Wave Wranglers": "GB",
  "Three Flying Fish": "GB",
  "Viva For Life": "BE"
};

function getFlag(teamName) {
  const code = TEAM_FLAGS[teamName];
  if (!code) return '';

  // Special flags for Wales and Scotland
  if (code === 'XW') return '🏴󠁧󠁢󠁷󠁬󠁳󠁿';
  if (code === 'XS') return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';

  // Convert country code to flag emoji (regional indicator symbols)
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

export class LeaderboardController {
  constructor() {
    this.panel = document.getElementById('leaderboard');
    this.list = document.getElementById('leaderboard-list');
    this.teamCount = document.getElementById('team-count');
    this.toggleBtn = document.getElementById('toggle-leaderboard');

    this.teams = [];
    this.selectedTeam = null;
    this.comparingTeams = new Set();

    // Callbacks
    this.onTeamSelect = null;
    this.onTeamCompare = null;

    this.init();
  }

  init() {
    // Toggle visibility
    this.toggleBtn.addEventListener('click', () => this.toggle());

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.panel.classList.contains('hidden')) {
        this.hide();
      }
    });
  }

  /**
   * Toggle panel visibility
   */
  toggle() {
    this.panel.classList.toggle('hidden');
    this.toggleBtn.classList.toggle('active');
  }

  /**
   * Show panel
   */
  show() {
    this.panel.classList.remove('hidden');
    this.toggleBtn.classList.add('active');
  }

  /**
   * Hide panel
   */
  hide() {
    this.panel.classList.add('hidden');
    this.toggleBtn.classList.remove('active');
  }

  /**
   * Update leaderboard with new team data
   */
  update(teams) {
    this.teams = teams;
    this.teamCount.textContent = `${teams.length} teams`;
    this.render();
  }

  /**
   * Render the leaderboard list
   */
  render() {
    // Sort by distance to finish (finished teams sorted by finish time)
    const sortedTeams = [...this.teams].sort((a, b) => {
      const aDtf = a.interpolated?.dtf ?? a.latest?.dtf ?? Infinity;
      const bDtf = b.interpolated?.dtf ?? b.latest?.dtf ?? Infinity;

      // Both finished: sort by finish time (earlier = better)
      if (aDtf === 0 && bDtf === 0) {
        const aTime = a.latest?.timestamp?.getTime() ?? 0;
        const bTime = b.latest?.timestamp?.getTime() ?? 0;
        return aTime - bTime;
      }

      // One finished, one racing: finished comes first
      if (aDtf === 0) return -1;
      if (bDtf === 0) return 1;

      // Both racing: sort by DTF
      return aDtf - bDtf;
    });

    // Create or update items
    this.list.innerHTML = '';

    sortedTeams.forEach((team, index) => {
      const rank = index + 1;
      const pos = team.interpolated || team.latest;
      const dtf = pos?.dtf ?? 0;
      const speed = pos?.speed ?? 0;

      const item = document.createElement('div');
      item.className = 'leaderboard-item';
      item.dataset.serial = team.serial;
      item.style.animationDelay = `${index * 0.03}s`;

      if (team.serial === this.selectedTeam) {
        item.classList.add('selected');
      }

      if (this.comparingTeams.has(team.serial)) {
        item.classList.add('comparing');
      }

      item.innerHTML = `
        <span class="team-rank ${rank <= 3 ? 'top-3' : ''}">${rank}</span>
        <div class="team-info">
          <div class="team-name">${getFlag(team.name)} ${this.escapeHtml(team.name)}</div>
          <div class="team-meta">
            <span class="team-color-dot" style="background-color: ${team.color}"></span>
            <span class="team-speed">${speed.toFixed(1)} kn</span>
          </div>
        </div>
        <div class="team-stats">
          <div class="team-dtf">${dtf.toFixed(0)}</div>
          <div class="team-dtf-label">nm</div>
        </div>
      `;

      // Click to select
      item.addEventListener('click', (e) => {
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          // Multi-select for comparison
          this.toggleCompare(team.serial);
        } else {
          // Single select
          this.selectTeam(team.serial);
        }
      });

      // Double-click to fly to location
      item.addEventListener('dblclick', () => {
        if (this.onTeamSelect) {
          this.onTeamSelect(team, true); // flyTo = true
        }
      });

      this.list.appendChild(item);
    });
  }

  /**
   * Select a team
   */
  selectTeam(serial) {
    // Toggle if already selected
    if (this.selectedTeam === serial) {
      this.selectedTeam = null;
    } else {
      this.selectedTeam = serial;
    }

    // Update UI
    this.list.querySelectorAll('.leaderboard-item').forEach(item => {
      item.classList.toggle('selected', item.dataset.serial == serial && this.selectedTeam);
    });

    // Callback
    if (this.onTeamSelect) {
      const team = this.teams.find(t => t.serial === serial);
      this.onTeamSelect(team, false);
    }
  }

  /**
   * Toggle team in comparison set
   */
  toggleCompare(serial) {
    if (this.comparingTeams.has(serial)) {
      this.comparingTeams.delete(serial);
    } else if (this.comparingTeams.size < 4) {
      this.comparingTeams.add(serial);
    }

    // Update UI
    this.list.querySelectorAll('.leaderboard-item').forEach(item => {
      item.classList.toggle('comparing', this.comparingTeams.has(parseInt(item.dataset.serial, 10)));
    });

    // Callback
    if (this.onTeamCompare) {
      const teams = [...this.comparingTeams].map(s => this.teams.find(t => t.serial === s)).filter(Boolean);
      this.onTeamCompare(teams);
    }
  }

  /**
   * Clear comparison selection
   */
  clearComparison() {
    this.comparingTeams.clear();
    this.list.querySelectorAll('.leaderboard-item').forEach(item => {
      item.classList.remove('comparing');
    });
  }

  /**
   * Set team select handler
   */
  setSelectHandler(handler) {
    this.onTeamSelect = handler;
  }

  /**
   * Set team compare handler
   */
  setCompareHandler(handler) {
    this.onTeamCompare = handler;
  }

  /**
   * Get selected team
   */
  getSelectedTeam() {
    return this.selectedTeam;
  }

  /**
   * Get comparing teams
   */
  getComparingTeams() {
    return [...this.comparingTeams];
  }

  /**
   * Escape HTML for safe rendering
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
